import { autorun, makeAutoObservable, reaction, runInAction } from 'mobx'
import type AppStore from './AppStore'
import { type Project } from './features'
import { type LoaderOptions } from '@loaders.gl/core'
import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { createAxiosInstance, setFromJSON } from 'helpers'

interface UserSettings {
  selectedProjectId?: string
}

export interface UserResponse {
  id: string
  email: string
  name: string
  settings: UserSettings
}

export interface UserPatchData {
  email?: string
  name?: string
  settings?: UserSettings
}

export interface UserList {
  users: UserResponse[]
}

export interface LoginCredentials {
  email: string
  password: string
}
export interface Token {
  access: string
  refresh: string
  time: number
}

class User implements UserResponse {
  constructor(
    private readonly store: UserStore,
    public readonly id: string,
    public email: string,
    public name: string,
    public settings: UserSettings
  ) {
    makeAutoObservable(this)
  }

  get initials(): string {
    const initials = this.name
      .split(' ')
      .map((s) => s[0])
      .join('')
    return initials.length > 0 ? initials.substring(0, 2).toUpperCase() : ''
  }

  get title(): string {
    return this.name.length > 0 ? this.name : this.email
  }

  updateCurrentProject(project: Project): void {
    this.settings.selectedProjectId = project.uuid
    void this.updateUserSettings()
  }

  async updateUserSettings(): Promise<AxiosResponse> {
    return await this.store.apiClient.patch(`/api/v1/user/${this.email}/`, {
      settings: this.settings
    })
  }

  async updateUserData(userData: UserPatchData): Promise<AxiosResponse> {
    const promise = this.store.apiClient.patch(
      `/api/v1/user/${this.email}/`,
      userData
    )

    void promise.then(() => {
      setFromJSON(this, userData)
    })

    return await promise
  }
}

class UserStore {
  public currentUser!: User | null
  public token: Token | null = null

  public appStore: AppStore

  constructor(appStore: AppStore) {
    const localToken = localStorage.getItem('token')
    if (localToken !== null) this.token = JSON.parse(localToken)

    this.appStore = appStore

    makeAutoObservable(this, {
      appStore: false
    })

    if (this.token !== null) {
      void this.refreshAccessToken()
        .then(() => {
          void this.setCurrentUser()
          this.appStore.fetchProjects()
        })
        .catch(() => {
          this.currentUser = null
        })
    } else {
      this.currentUser = null
    }

    autorun(
      () => {
        if (this.currentUser === null || this.currentUser === undefined) return

        void this.refreshAccessToken()
      },
      {
        scheduler: (run) => setTimeout(run, 250000)
      }
    )

    reaction(
      () => this.accessToken, // only trigger this reaction when the access token gets available the first time

      () => {
        if (this.accessToken === undefined) return
        this.appStore.fetchProjects()
      }
    )
  }

  get apiClient(): AxiosInstance {
    return createAxiosInstance(
      process.env.REACT_APP_API_URL ?? '',
      this.accessToken
    )
  }

  async loginWithEmailAndPassword(
    login: LoginCredentials
  ): Promise<AxiosResponse> {
    const promise = axios.post(
      `${process.env.REACT_APP_API_URL ?? ''}` + '/api/v1/auth/jwt/create/',
      login
    )
    promise
      .then((res) => {
        runInAction(() => {
          this.token = { ...res.data, time: Date.now() }
        })
        localStorage.setItem('token', JSON.stringify(this.token))
        void this.setCurrentUser()
        this.appStore.fetchProjects()
      })
      .catch(() => {
        console.info('Login failed.')
      })
    return await promise
  }

  logout(): void {
    runInAction(() => {
      this.token = null
      this.currentUser = null
    })
    localStorage.removeItem('token')
  }

  async refreshAccessToken(): Promise<AxiosResponse> {
    const promise = this.apiClient.post('/api/v1/auth/jwt/refresh/', {
      refresh: this.token?.refresh
    })

    void promise
      .then((res) => {
        runInAction(() => {
          this.token = {
            ...this.token,
            ...res.data,
            time: Date.now()
          }
          localStorage.setItem('token', JSON.stringify(this.token))
        })
      })
      .catch(() => {
        this.logout()
      })
    return await promise
  }

  get accessToken(): string | undefined {
    return this.token?.access
  }

  get loadOptions(): LoaderOptions | undefined {
    if (this.accessToken === undefined) return
    return {
      fetch: {
        headers: {
          Authorization: 'Bearer ' + this.accessToken
        }
      }
    }
  }

  async setCurrentUser(): Promise<AxiosResponse> {
    const promise = this.apiClient.get<UserResponse>('/api/v1/user/me/')

    promise
      .then((res) => {
        runInAction(() => {
          this.currentUser = new User(
            this,
            res.data.id,
            res.data.email,
            res.data.name,
            res.data.settings
          )
        })
      })
      .catch(() => {
        this.logout()
        window.location.href = '/'
      })

    return await promise
  }
}

export default UserStore
